import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Fab from '@material-ui/core/Fab';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import NewReleasesIcon from '@material-ui/icons/NewReleasesOutlined';
import { MarkdownContent, sidebarConfig } from '@backstage/core-components';
import { thanksName, type ReleaseNotes } from './whatsNew';

const useStyles = makeStyles(theme => ({
  subtitle: { color: theme.palette.text.secondary, fontSize: 13 },
  body: {
    '& h2': { fontSize: 16, marginTop: theme.spacing(2) },
    '& h3': { fontSize: 15, marginTop: theme.spacing(2) },
  },
  thanks: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(0, 213, 190, 0.12)'
        : 'rgba(0, 150, 137, 0.08)',
  },
  thanksList: { margin: theme.spacing(0.5, 0, 0), paddingLeft: 18 },
  github: { marginRight: 'auto' },
  launcher: {
    position: 'fixed',
    // Bottom left of the page, right of the closed sidebar.
    left: sidebarConfig.drawerWidthClosed + theme.spacing(2),
    bottom: theme.spacing(3),
    zIndex: theme.zIndex.appBar,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    textTransform: 'none',
  },
}));

const publishedOn = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
};

/**
 * « Nouveautés » modal: the GitHub release notes of the deployed version, and thanks to the
 * people whose issues it delivers. An important release offers a tour of its sections.
 */
export function WhatsNewDialog({
  notes,
  open,
  onClose,
  onTour,
}: {
  notes: ReleaseNotes;
  open: boolean;
  onClose: () => void;
  onTour: () => void;
}) {
  const classes = useStyles();
  const date = publishedOn(notes.publishedAt);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="whats-new-title"
    >
      <DialogTitle id="whats-new-title" disableTypography>
        <Typography variant="h6">Nouveautés du portail</Typography>
        <div className={classes.subtitle}>
          {notes.name}
          {date && ` · ${date}`}
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.body}>
          <MarkdownContent content={notes.body} linkTarget="_blank" />
        </div>
        {notes.thanks.length > 0 && (
          <div className={classes.thanks}>
            <strong>Merci</strong> à celles et ceux dont les idées sont dans
            cette version :
            <ul className={classes.thanksList}>
              {notes.thanks.map(t => (
                <li key={t.login}>
                  <Link href={t.url} target="_blank" rel="noopener noreferrer">
                    {thanksName(t)}
                  </Link>{' '}
                  pour{' '}
                  {t.issues.map((issue, i) => (
                    <span key={issue.number}>
                      {i > 0 && ', '}
                      <Link
                        href={issue.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        #{issue.number}
                      </Link>{' '}
                      « {issue.title.replace(/^\[[^\]]+\]\s*/, '')} »
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.github}
          href={notes.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Voir sur GitHub
        </Button>
        <Button onClick={onClose}>Fermer</Button>
        {notes.important && (
          <Button variant="contained" color="primary" onClick={onTour}>
            Visite des nouveautés
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Bottom left button of an important release, until its tour is done (whatsNew.ts). */
export function WhatsNewLauncher({
  notes,
  onClick,
}: {
  notes: ReleaseNotes;
  onClick: () => void;
}) {
  const classes = useStyles();
  return (
    <Fab
      variant="extended"
      size="small"
      className={classes.launcher}
      onClick={onClick}
    >
      <NewReleasesIcon fontSize="small" style={{ marginRight: 6 }} />
      Nouveautés {notes.version}
    </Fab>
  );
}
